/**
 * English — the reference dictionary. Every key used in the app must exist
 * here; other locales fall back to English per-key.
 *
 * Copy rules (see docs/PRODUCT_COPY.md): honest about uncertainty, no
 * scientific-proof claims, no deterministic promises.
 */
export const en = {
  'app.name': 'Astrally',
  'app.tagline': 'Your kundli. Your Ally. Your people.',
  'app.subtitle': 'Jyotish, Western Astrology & AI Connections',

  'tabs.home': 'Home',
  // The Home tab is labelled "Today" because it opens on today's reading —
  // see the arrangement note at the top of components/TabBar.tsx.
  'tabs.today': 'Today',
  'tabs.ally': 'Ally',
  'tabs.ai': 'Astrally AI',
  'tabs.charts': 'Kundli',
  'tabs.discover': 'Discover',
  'tabs.messages': 'Messages',
  'tabs.profile': 'Profile',

  // Surfaces without a tab of their own. Reached in one tap from Home.
  // "Rashifal" — the everyday Indian word for the daily reading. The screen
  // also carries the Western tab, which names itself on its own card.
  'nav.horoscope': 'Rashifal',
  'nav.journal': 'Journal',
  'nav.compatibility': 'Compatibility',
  'nav.muhurta': 'Good timing',

  // The two traditions. Always name them — a reader with no background does
  // not know that "Vedic" and "sidereal" are the same conversation.
  'system.vedic': 'Vedic',
  'system.western': 'Western',
  'system.vedic.long': 'Vedic (Indian astrology)',
  'system.western.long': 'Western astrology',

  // Commercial
  'unlock.title': 'Unlock Astrally',
  'unlock.body':
    'One small purchase permanently unlocks the complete astrology utility and Astrally AI — on Android and web, tied to your account, forever. Connections stay optional and off until you choose otherwise.',
  'unlock.includes': 'Included with Base',
  'unlock.cta': 'Unlock for',
  'unlock.note.requests':
    'Base includes 5 lifetime outgoing connection requests. Incoming requests, accepting and existing chats are always unlimited.',
  'pro.title': 'Astrally Pro',
  'pro.tagline': 'Fair-use unlimited AI chat and the full compatibility-ranked social graph.',
  'pro.monthly': 'Monthly',
  'pro.annual': 'Annual',
  'pro.requests': 'outgoing connection requests per day',
  'pro.upgrade': 'Upgrade to Pro',
  'pro.active': 'Pro active',
  'credits.title': 'AI credits',
  'credits.body':
    'Credits power premium generations: Go Deeper answers, long reports and Master Readings. Purchased credits never expire.',
  'credits.balance': 'Balance',
  'credits.buy': 'Buy',
  'credits.ledger': 'History',
  'credits.costs': 'What credits buy',

  // Home
  'home.today': 'Today for you',
  'home.transit': 'Your strongest transit',
  'home.ask': 'Ask Astrally',
  'home.people': 'People aligned with you',
  'home.requests': 'Connection requests',
  'home.continue': 'Continue your reading',
  'home.seeMore': 'See more in Discover',
  'home.fullReading': 'Full reading',
  'home.calculating': 'Your kundli is still being calculated.',
  'home.explore': 'Explore',
  'home.credits': '{n} credits',
  'home.creditsOne': '1 credit',
  'home.pendingRequests': '{n} pending requests',
  'home.pendingRequestsOne': '1 pending request',
  'home.unreadMessages': '{n} unread messages',
  'home.unreadMessagesOne': '1 unread message',
  // Why the button exists at all: the reading above it is already complete.
  'home.narrateHint':
    'Everything above is already calculated. Want Ally to put it together in one piece?',
  'home.moonUncertain':
    'A Jyotish reading is counted from your Chandra rashi — the rashi your Moon held at birth — and without a confirmed birth time your Moon could land in either of two rashis. Rather than pick one and read a whole day off a coin flip, Astrally leaves this side blank. The Western reading still works — it reads the whole chart, not just the Moon.',

  // Home — panchanga (today's almanac). Deterministic, offline, free.
  'home.panchanga': "Today's panchanga",
  'home.sunrise': 'Sunrise',
  'home.muhurtaCta': 'Find a good time for something',
  'panchanga.tithi': 'Tithi',
  'panchanga.nakshatra': 'Nakshatra',
  'panchanga.yoga': 'Yoga',
  'panchanga.karana': 'Karana',
  'panchanga.vaara': 'Weekday',
  'panchanga.rahuKaal': 'Rahu kaal',
  'panchanga.abhijit': 'Abhijit muhurta',

  // Home — the accuracy loop's entry point.
  'home.rateTitle': 'Did yesterday land?',
  'home.rateYesterday': 'Yesterday, Astrally read this in your kundli',
  'home.rateCta': 'Rate it',
  'home.rateSkip': 'Skip',

  // Home — conversation trees.
  'home.treeTitle': 'Keep going',
  'home.treeNew': 'New',
  'home.treeDepth': 'Depth {n}',
  'home.treeContinue': 'Continue',
  'home.treeStart': 'Start here',

  // Private by default / social opt-in
  'social.private.badge': 'Private',
  'social.private.title': 'Your profile is private',
  'social.private.body':
    'Astrally starts private. Nobody can see you, no compatibility is calculated about you, and no one can send you a request. Your kundli and Astrally AI work fully — connections are simply switched off.',
  'social.private.cta': 'Explore connections',
  'social.optin.title': 'Turn on connections',
  'social.optin.currently':
    'Right now your profile is private — you are invisible to everyone on Astrally.',
  'social.optin.whatChanges': 'What turning this on does',
  'social.optin.whatNever': 'What never changes',
  'social.optin.intents': 'What are you open to?',
  'social.optin.intentsHelp':
    'Pick at least one. This is the only thing matching uses to decide who you might meet — change it any time.',
  'social.optin.required': 'Required',
  'social.optin.needIntents': 'Pick at least one thing you are open to above — tap any of them.',
  'social.optin.needConsent': 'Tick the consent box above so Astrally may recommend people to you.',
  'social.optin.needBoth':
    'Two things left: pick at least one thing you are open to, and tick the consent box.',
  'social.optin.selected': '{n} selected',
  'social.optin.cta': 'Turn on connections',
  'social.optin.stayPrivate': 'Stay private for now',
  'social.disable.title': 'Turn off connections',
  'social.disable.confirm':
    'Turn connections off? You disappear from Discover immediately, pending requests are withdrawn, and nobody can reach you. Your kundli, Astrally AI and memories are untouched.',
  'social.disable.keepChats':
    'Conversations you have already accepted stay reachable so nobody is left mid-conversation — end them individually if you prefer.',
  'social.status.on': 'Connections are on',
  'social.status.off': 'Connections are off',

  // Discover
  'discover.title': 'Discover',
  'discover.subtitle': 'Ranked by fit — never compatibility alone.',
  'discover.connect': 'Connect',
  'discover.pass': 'Pass',
  'discover.save': 'Save',
  'discover.view': 'View profile',
  'discover.remaining': 'connection requests remaining',
  'discover.empty': 'No more profiles today. Fresh discovery arrives tomorrow.',
  'discover.introPrompt': 'Add a short note (optional)',
  'discover.confirm': 'Send request',
  'discover.whyWork': 'Why you may work well',
  'discover.friction': 'Potential friction',
  'discover.breakdown': 'Compatibility breakdown',
  'discover.upgradeFull': 'Astrally Pro unlocks the complete compatibility breakdown.',

  // Astrally AI tiers
  'ai.deeper': 'Go Deeper',
  'ai.master': 'Master Reading',
  'ai.deeper.desc': 'A deeper, more layered reading of this question.',
  'ai.master.desc': 'A comprehensive multi-system life reading — our most advanced generation.',
  'ai.cost.confirm': 'This uses {n} credits. Continue?',
  'ai.master.confirm2': 'A Master Reading is our most substantial generation. Confirm {n} credits?',
  'ai.refunded': 'Generation failed — your credits were automatically restored.',

  // Requests
  'req.incoming': 'Incoming requests',
  'req.outgoing': 'Sent requests',
  'req.withdraw': 'Withdraw',
  'req.pendingState': 'Request pending',
  'req.unavailable': 'Request unavailable',

  // Onboarding
  'onb.welcome.title': 'Your kundli, and an AI that reads it with you',
  'onb.welcome.body':
    'Astrally calculates your full kundli — and the Western chart alongside it — and Astrally AI interprets them with you, learning what matters to you over time. This part is yours alone. Nobody else can see it.',
  'onb.welcome.optionalBadge': 'Optional · off by default',
  'onb.welcome.optionalTitle': 'Meeting people is entirely your choice',
  'onb.welcome.optionalBody':
    'Astrally can also introduce you to a small number of compatible people — but only if you ask it to. You start invisible: no profile is shared, nobody can find you, and nobody can contact you. Turn it on whenever you like from your profile, and off again just as easily.',
  // Deliberately NOT "no feed, no swiping, ever". That promised the absence of
  // features the product may well ship, and a promise you break is worse than
  // one you never made. What IS permanent is the default and the control, so
  // that is what this claims.
  'onb.welcome.noFeed': 'Private by default. You stay invisible until you decide otherwise.',
  'onb.start': 'Get started',
  'onb.name': 'What should Ally call you?',
  'onb.name.ph': 'Display name',
  'onb.dob': 'Date of birth',
  'onb.place': 'Birthplace',
  'onb.place.search': 'Search city…',
  'onb.place.manual': 'Enter coordinates manually',
  'onb.timeStatus': 'Your birth time',
  'onb.time.exact': 'I know my exact birth time',
  'onb.time.approx': 'I know an approximate time or range',
  'onb.time.unknown': "I don't know my birth time",
  'onb.time.exact.note':
    'Your complete kundli unlocks: your Lagna, all twelve bhavas, divisional charts and the dasha timeline — plus the Western rising sign and houses.',
  'onb.time.approx.note':
    "We'll calculate what stays stable across your time window and clearly mark what changes. Nothing is silently assumed.",
  'onb.time.unknown.note':
    "You don't need to guess. We'll calculate what remains reliable for the whole day and leave the Lagna and bhavas unassigned — never silently substituted.",
  'onb.window.label': 'Approximate window',
  'onb.window.morning': 'Morning (05:00–11:00)',
  'onb.window.afternoon': 'Afternoon (11:00–17:00)',
  'onb.window.evening': 'Evening (17:00–22:00)',
  'onb.window.night': 'Night (22:00–05:00)',
  'onb.window.custom': 'Custom range',
  'onb.language': 'Preferred language',
  'onb.city': 'Where do you live now?',
  'onb.ageConfirm': 'I confirm I am 18 or older',
  'onb.ageConfirm.why':
    'Astrally includes private introductions between people, so it is adults-only.',
  'onb.intents': 'What kinds of connections are you open to?',
  'onb.consent.title': 'Private recommendations',
  'onb.consent.body':
    'Astrally privately recommends a small number of people based on mutual intentions, communication fit, interests and kundli compatibility. There is no public feed. By turning this on, you allow Astrally to use your approved profile, kundli-derived features and conversations with Astrally AI to make private recommendations. You can turn it off again at any time.',
  'onb.consent.accept': 'I understand — continue',
  'onb.private.title': 'You start private',
  'onb.private.body':
    'Your profile is private by default. Your kundli and Astrally AI are yours alone — nobody can see you, and no one can contact you. If you ever want to meet compatible people, you can turn connections on from your profile.',
  'onb.finish': 'Calculate my kundli',
  'onb.calculating': 'Calculating your kundli…',

  // Chart modes
  'chart.mode.exact.title': 'Your complete kundli is ready.',
  'chart.mode.exact.body':
    'Because you provided an exact birth time, Astrally can calculate your Lagna, bhavas, divisional charts and dasha timeline — plus the Western rising sign and houses.',
  'chart.mode.approx.title': 'Calculated across your time window',
  'chart.mode.approx.body':
    "We'll calculate the parts of your kundli that remain stable across this time window. The Lagna, the bhavas and some divisional charts may change, so Astrally shows the possible results instead of pretending one is certain.",
  'chart.mode.unknown.title': 'Whole-day kundli — no guessed birth time',
  'chart.mode.unknown.body':
    'Astrally shows what remains reliable for the entire day and clearly marks what could change. The Lagna and bhavas stay hidden until your time is narrowed.',
  'chart.stable': 'Stable across your time window',
  'chart.stable.day': 'Stable across the whole day',
  'chart.possible': 'Possible depending on your exact time',
  'chart.unavailable': 'Unavailable until the time is narrowed',
  'chart.moon.multi':
    'Your Chandra (Moon) may fall in either of these positions depending on when you were born. Ally will interpret both possibilities and explain what remains common.',
  'chart.reference.label': 'Reference kundli — not an exact birth kundli',
  'chart.tab.jyotish': 'Jyotish',
  'chart.tab.western': 'Western',
  'chart.tab.combined': 'Combined',
  'chart.dasha.title': 'Vimshottari dasha timeline',
  'chart.gochar': 'Current transits (gochar)',
  'chart.aspects': 'Aspects',
  'chart.refine.cta': 'Narrow my birth time',
  'chart.window.adjust': 'Adjust your time window',

  // Ally
  'ally.grounding':
    'Ally interprets your calculated kundli — it never generates graha positions from a prompt.',
  'ally.placeholder': 'Ask about your kundli, your patterns, your people…',
  'ally.sources': 'Sources',
  'ally.suggest.personality': 'What does my kundli say about me?',
  'ally.suggest.career': 'Career themes right now?',
  'ally.suggest.dasha': 'What period am I in?',
  'ally.suggest.uncertain': 'What can you tell without my birth time?',
  // Chart-aware starters — filled from the grounding fact pack, no AI call.
  'ally.suggest.dashaLord': 'What does my {lord} period mean for me now?',
  'ally.suggest.gocharSaturn': 'Shani is in {sign} — what is it asking of me?',
  'ally.suggest.gocharJupiter': 'Guru is in {sign} — where does that open things up?',
  'ally.suggest.aspect': 'What does my {aspect} say about me?',
  'ally.suggest.yoga': 'Tell me about my {yoga}.',
  'ally.rec.why': 'Why this person?',
  'ally.rec.request': 'Request introduction',
  'ally.rec.skip': 'Not relevant',
  'ally.rec.notSimilar': "Don't recommend similar",
  'ally.rec.report': 'Report concern',
  'ally.rec.requested': 'Introduction requested — they can accept or decline privately.',
  'ally.memory.saved': 'Suggested memory — review in Profile › What Ally knows about me',

  // Astrally AI conversations (the chat list drawer). Everything here is read
  // off the device — opening the list, renaming or deleting never costs a
  // message.
  'chats.title': 'Chats',
  'chats.aria': 'Your Astrally AI chats',
  'chats.open': 'Your chats ({n})',
  'chats.new': 'New chat',
  // Which astrologer a conversation is with. Jyotish is the default voice;
  // Western is the opt-in side path.
  'chats.personaJyotish': 'Jyotish astrologer',
  'chats.personaWestern': 'Western astrologer',
  'chats.newAria': 'Start a new chat',
  'chats.none': 'Nothing saved yet',
  'chats.countOne': '1 conversation on this device',
  'chats.countMany': '{n} conversations on this device',
  'chats.empty':
    'Every conversation you start is kept here, on your phone, so you can pick one back up later. Ask Astrally something and this list fills itself in.',
  'chats.earlier': 'Your earlier chats',
  'chats.rename': 'Rename',
  'chats.nameAria': 'Chat name',
  'chats.deleteConfirm':
    'Delete “{title}”? Its messages are only on this device, so this cannot be undone.',
  'chats.keep': 'Keep it',
  'chats.delete': 'Delete chat',
  'chats.messagesOne': '1 message',
  'chats.messagesMany': '{n} messages',
  'chats.rowEmpty': 'empty',
  'chats.youPrefix': 'You: ',
  // Coarse relative times for a list row — a glance, not a log.
  'chats.time.now': 'just now',
  'chats.time.minutes': '{n}m ago',
  'chats.time.hours': '{n}h ago',
  'chats.time.yesterday': 'yesterday',
  'chats.time.days': '{n}d ago',

  // Conversation trees — the four guided paths inside the Ally tab. Only the
  // chrome lives here; every path's own title, blurb and beats are authored
  // content in packages/shared, next to the DAG they belong to.
  'trees.title': 'Paths into your kundli',
  'trees.open': 'Walk a path',
  'trees.hide': 'Hide',
  'trees.answerPlaceholder': 'Type your answer to Ally…',

  // Memory
  'memory.title': 'What Ally knows about me',
  'memory.empty': 'Nothing yet. As you talk to Ally, it will suggest memories here for your approval.',
  'memory.suggested': 'Waiting for your approval',
  'memory.confirmed': 'Confirmed memories',
  'memory.confirm': 'Keep',
  'memory.reject': 'Discard',
  'memory.delete': 'Delete',
  'memory.edit': 'Edit',
  'memory.sensitive': 'Sensitive — stored only if you approve',
  'memory.add': 'Add a memory yourself',

  // Connections / intros
  'intro.incoming.title': 'Introduction requests',
  'intro.incoming.empty': 'No pending requests.',
  'intro.accept': 'Accept',
  'intro.decline': 'Decline',
  'intro.accepted': 'Connected — a private conversation is now open.',
  'intro.declined': 'Declined privately. They will not be told why.',

  // Messages
  'msg.title': 'Messages',
  'msg.empty':
    'Conversations open here after a mutual introduction. Ally never reads these messages.',
  'msg.placeholder': 'Message…',
  'msg.block': 'Block',
  'msg.report': 'Report',
  'msg.end': 'End connection',
  'msg.ended': 'This conversation has ended.',
  'msg.askAlly': 'Ask Ally for help (visible to you only)',
  // Moderation refusals. Deliberately short and non-preachy: say what happened
  // and what to do, do not lecture someone who may simply have phrased
  // something badly. The draft stays in the composer either way.
  'msg.moderation.blocked':
    'Not sent — this looks like it breaks our community rules. Edit it and try again.',
  'msg.moderation.unavailable':
    'Messages can’t be checked right now, so this one wasn’t sent. Your text is still here — try again in a moment.',

  /* --- chat attachments -------------------------------------------------------

     Sending a photo or video is Pro. OPENING one is Pro too, and those are two
     different people's subscriptions — which is why so much of the copy below
     is about the recipient rather than the sender. A photo that lands as a blur
     nobody mentions is the failure this whole feature is shaped around: a
     conversation where one side does not know their photo was never seen is
     worse than not allowing the photo at all.

     PHOTOS ARE CHECKED. VIDEO IS NOT. The asymmetry is real and the copy
     states it, because a reader deciding whether to open a file from a
     near-stranger is entitled to know which kind of file they are holding.

     Photo: `checkImage` is implemented on the provider in
     functions/src/safety/moderation.ts, and it was verified live on
     2026-08-09 — the vendor's response named `image` as the modality its
     classifiers ran against, which is the only evidence that means anything
     here. See the block in functions/src/media/mediaFns.ts for why: a period
     of this product's life had the capability wired up and NOTHING screened,
     because an unfunded account returns a bare 429 that is indistinguishable
     from a rate limit, so every gate stayed green while every image went
     `unchecked`.

     Video: no provider in this product can see inside a clip. That is a
     capability that does not exist, so the copy says so plainly.

     NOTE THE HEDGE IN `photoNote`, and do not remove it. The check FAILS OPEN
     by design — a moderation outage must not become a messaging outage — so
     "usually checked" is the strongest honest claim a static string can make.
     The per-photo truth lives on the message document (`moderation: 'clean' |
     'unchecked'`), the only place it is actually known. An earlier version of
     this string promised photos "are checked automatically before they send"
     at a time when nothing checked anything; the wording below is deliberately
     the kind that stays true whether or not the vendor answered. */
  'msg.media.attach': 'Attach a photo or video',
  'msg.media.photo': 'Photo',
  'msg.media.video': 'Video',
  'msg.media.remove': 'Remove',
  'msg.media.someone': 'The other person',
  'msg.media.proOnly':
    'Photos and videos in chat are part of Astrally Pro — sending them and opening them both. Everything else in this conversation works exactly the same.',
  'msg.media.photoNote':
    'Photos are resized on your phone, then screened automatically before they send — though a screen can be skipped if the checker is unreachable, and it never catches everything. Report anything that doesn’t belong from the ⋯ menu.',
  'msg.media.videoNote':
    'Video can be up to {seconds} seconds, and is stored exactly as you recorded it. Videos are not checked automatically — nothing can check a clip, so nobody has looked at it.',
  'msg.media.viewerNote':
    'Only Pro members can open a photo or video. If {name} isn’t on Pro, it arrives as a blurred placeholder.',
  'msg.media.demo':
    'Demo mode keeps everything on this device and has nowhere to store a file, so attachments are off here.',
  'msg.media.readyPhoto': 'Photo ready to send · {size}KB',
  'msg.media.readyVideo': 'Video ready to send · {seconds}s · {size}KB',
  'msg.media.videoFormat':
    'That video format can’t be sent. Record or export it as an MP4 and try again.',
  'msg.media.needSomething': 'Write a message, or attach a photo or video.',
  // Pressing Enter while a file is still being resized used to send the caption
  // on its own and drop the picture without a word. Saying so beats both the
  // silent drop and a dead key that looks broken.
  'msg.media.stillPreparing':
    'That file is still being prepared. Give it a second and send again, so it goes with your message.',
  'msg.media.proToSend':
    'Sending photos and videos is part of Astrally Pro. Your message is still here — send it as text, or open Pro from your profile.',
  // The answer the server gives at the moment of sending.
  'msg.media.lockedNow':
    '{name} can’t open that — they aren’t on Pro, so it arrived as a blurred placeholder. It unlocks for them if they subscribe.',
  // The durable copy, rendered on the bubble. PAST TENSE on purpose:
  // mediaLockedFor is a snapshot of who could not open it when it was sent, and
  // someone who subscribes tomorrow can open everything they were sent
  // yesterday — Storage rules are evaluated when the file is fetched.
  'msg.media.lockedThen':
    '{name} couldn’t open this when you sent it — it arrived blurred. It unlocks for them if they get Pro.',
  // No answer available (demo mode, or a build that predates the media reply).
  // Honest and unconditional rather than a guess dressed as a fact.
  'msg.media.reachUnknown':
    'Sent. Only Pro members can open a photo or video — if {name} isn’t on Pro, this arrived as a blurred placeholder.',
  'msg.media.videoUnchecked': 'Video isn’t checked automatically — nothing has looked at this clip.',

  // Profile
  'profile.title': 'Profile',
  'profile.editIntents': 'Connection intentions',
  'profile.pause': 'Pause recommendations',
  'profile.pause.note': 'While paused, you are not shown to others and receive no new recommendations.',
  'profile.memory': 'What Ally knows about me',
  'profile.privacy': 'Privacy & data',
  'profile.privacy.birth':
    'Your exact birth details stay on this device. Only derived kundli features sync to enable matching.',
  'profile.export': 'Export my data',
  'profile.deleteAccount': 'Delete account',
  'profile.deleteAccount.confirm':
    'This permanently deletes your profile, memories, recommendations and conversations. This cannot be undone.',
  'profile.subscription': 'Subscription',
  'profile.refine': 'Birth-time refinement',
  'profile.switchDemo': 'Switch demo account',
  'profile.blocked': 'Blocked users',

  // Refinement
  'refine.title': 'Narrow your birth time',
  'refine.body':
    'Add dated life events and Astrally will score candidate birth-time windows against your dasha and transit timeline. The result is an estimate — never a certainty.',
  'refine.addEvent': 'Add event',
  'refine.run': 'Score my time windows',
  'refine.disclaimer':
    'Based on the events you supplied, these windows fit better than the alternatives. Treat them as estimates until confirmed from records or further events.',
  'refine.accept': 'Use this window',
  'refine.reject': 'Keep my current setting',
  'refine.changes': 'What changed after refining',

  // Subscription
  'sub.title': 'One subscription. The complete product.',
  'sub.body': 'Jyotish, Western astrology, Ally, memory, recommendations, introductions and messaging — one plan.',
  'sub.trial.active': 'Trial active',

  // Safety
  'safety.disclaimer':
    'Astrology in Astrally is a way of reading meaning, not scientific proof. Compatibility signals are never proof that a person is safe or right for you.',

  // Common
  'common.continue': 'Continue',
  'common.back': 'Back',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.done': 'Done',
  'common.loading': 'Loading…',
  'common.error': 'Something went wrong. Try again.',
  'common.demoMode': 'Demo mode — deterministic mock AI, local data only',
  'common.retry': 'Try again',
  'common.close': 'Close',

  // Appearance
  'theme.title': 'Appearance',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.textSize': 'Text size',
  'theme.textSmaller': 'Smaller text',
  'theme.textLarger': 'Larger text',
  'theme.textSizeHelp':
    'Applies everywhere in Astrally and is remembered on this device. It multiplies your phone or browser text size rather than replacing it.',
  'theme.systemHelp': 'Following your device setting.',
  'theme.manualHelp': 'Astrally stays on this theme whatever your device does.',

  // Glossary / explanations
  'glossary.title': 'Astrology glossary',
  'glossary.subtitle': 'Every term Astrally uses, in plain language.',
  'glossary.search': 'Search terms…',
  'glossary.seeAll': 'See all terms',
  'glossary.empty': 'No term matches that search.',
  'glossary.whatIsThis': 'What is this?',
  'glossary.moreOnTerm': 'More about {term}',
  'glossary.startCurriculum': 'New to all of this? Start with the curriculum',

  // Learn — the guided curriculum (route /learn). Only short UI strings live
  // here; lesson content stays in features/learn/curriculum.ts per the
  // long-form-content rule.
  'learn.title': 'Learn astrology',
  'learn.subtitle': 'Indian astrology first, Western optional — and nothing here is locked.',
  'learn.progress': '{done} of {total} lessons',
  'learn.module': 'Module {n}',
  'learn.minutes': '{n} min',
  'learn.start': 'Start the first lesson',
  'learn.continue': 'Continue learning',
  'learn.allDone': 'Every lesson done — the whole map is yours.',
  'learn.checkpoint': 'Checkpoint',
  'learn.checkpointHint': 'Answer the checkpoint to mark this lesson done.',
  'learn.correct': 'Exactly right.',
  'learn.notQuite': 'Not quite — here is the idea.',
  'learn.next': 'Next lesson',
  'learn.prev': 'Previous',
  'learn.done': 'Finish — back to all modules',
  // Learn is a contents page, not a track: these say so out loud, because the
  // old screen only ever offered "continue" and read as a forced path.
  'learn.contents': 'All lessons',
  'learn.anyOrder':
    'Contents, not a course. Every lesson stands on its own — start wherever you like.',
  'learn.search': 'Search lessons and terms…',
  'learn.searchEmpty': 'Nothing matches that. Try a word like “dasha”, “nakshatra” or “matching”.',
  'learn.optional': 'Optional',
  'home.keepLearning': 'Keep learning',

  // Greetings — hardcoded English previously, so they never translated.
  'home.greeting.morning': 'Good morning',
  'home.greeting.afternoon': 'Good afternoon',
  'home.greeting.evening': 'Good evening',
  'home.greeting.night': 'Good evening',
  'home.askAstrally': 'Ask Astrally',
  'home.readToday': 'Read today for me',
  'home.todayTitle': 'Today for you',
  'home.aiThinking': 'Astrally is reading your kundli…',
  'home.openChat': 'Continue in Astrally AI',
  'home.continueBody': 'Pick up where you left off',

  // Fair use — replaces internal marketing commentary that shipped to users.
  'pro.fairUse':
    'Fair-use limits keep Astrally fast for everyone. Normal daily use is never restricted.',
  'unlock.pricePending': 'Loading price…',
  'unlock.unavailable':
    'Purchases are temporarily unavailable. Nothing was charged — please try again shortly.',

  // Requirement messages for forms that used to disable their button silently.
  'form.needName': 'Please enter at least 2 characters so Astrally knows what to call you.',
  'form.needEmail': 'Enter a valid email address.',
  'form.needPassword': 'Your password needs at least 6 characters.',
  'form.needCity': 'Pick your birth city from the list of results.',
  'form.needEvent': 'Add at least one dated life event first — that is what the scoring compares.',
  'msg.conversationEmpty': 'No messages yet. Say hello.',
  'discover.loadFailed': 'Could not load your matches.',
  'profile.gone': 'This profile is no longer available.',
  'chart.failed': 'Your kundli could not be loaded.',
  'chart.recalculate': 'Recalculate my kundli',

  // A quiet day is a real reading, not a failure to produce one — say so
  // plainly rather than leaving the card blank.
  'home.startLearning': 'New to astrology?',
  'home.startLearningTitle': 'Learn the basics, from zero',
  'home.startLearningBody':
    'Short lessons that explain every word this app uses — no background needed.',
  'onb.haveAccount': 'I already have an account',
  'onb.restore.title': 'Welcome back — let’s rebuild your kundli',
  'onb.restore.body':
    'You’re signed in. Astrally keeps your birth date, time and place on your device and never uploads them, so they aren’t waiting on the server — enter them once here and this device is set up for good.',

  'horoscope.quietDay':
    'Nothing is pulling hard at your chart today. Quiet stretches are as real as loud ones.',
  'horoscope.sadeSati':
    'You are in Sade Sati — the roughly seven-and-a-half years Shani spends crossing your Chandra rashi and the rashis either side of it.',

  /* ANCHOR BLOCKS — several agents add keys to this file concurrently, and a
     shared "append at the end" anchor is a write race. Each block below is
     owned by exactly one change; keys go INSIDE the owner's block only. */

  // infra a11y keys
  'glossary.fullStory': 'Full story',
  'glossary.westernEquiv': 'Western: {name}',

  // === terminology-sweep keys (owner: terminology sweep) ===
  // === interactive-kundli keys (owner: interactive kundli) ===
  'kundli.explainToggle': 'Show where to tap on this kundli',
  'kundli.pop.rashiNum': 'rashi {n}',
  'kundli.pop.empty': 'No graha sits here. Empty bhavas are normal — every kundli has several.',
  'kundli.homeTitle': 'Your kundli',
  'kundli.homeHintPre': 'Tap any ',
  'kundli.homeHintPost': ' to see what lives there.',
  'kundli.homeOpen': 'Open the full kundli',
  'kundli.homeNoLagna':
    'Without an exact birth time your kundli has no Lagna to anchor its diamond, so Astrally does not guess one. Open the Kundli tab to see which parts of your kundli hold steady across your birth window.',
  'kundli.homeCalculating': 'Your kundli is still being calculated.',
  'kundli.aiReading': 'Astrally is reading your kundli…',
  'kundli.rateYesterday': 'Yesterday, Astrally read this in your kundli',
  'kundli.moonUncertain':
    'Vedic readings are counted from the rashi your Moon occupied at birth, and without a confirmed birth time your Moon could land in either of two rashis. Rather than pick one and read a whole day off a coin flip, Astrally leaves this side blank. The Western reading above still works — it reads your whole chart, not just the Moon.',
  'kundli.legend.north': 'Small corner number = bhava · large number = rashi',
  'kundli.legend.fixed':
    'Small corner number = bhava, counted from your Lagna · each box holds one rashi',
  'kundli.legend.noLagna':
    'Each box holds one rashi. Bhava numbers need a Lagna, so this kundli leaves them out.',
  'kundli.howToRead': 'How to read this',
  // === kundli-depth keys (owner: charts depth) ===
  'kundli.title': 'Kundli',
  'kundli.guide.toggle': 'How to read this kundli',
  'kundli.guide.learn': 'Learn this properly',
  'kundli.grahaPositions': 'Graha positions',
  'kundli.vargaCharts': 'Varga charts',
  'kundli.gochar': 'Gochar (current transits)',
  // === learn-visuals keys (owner: learn visuals) ===
  // Captions and labels for the schematic Learn diagrams. Each figure has an
  // `.alt` (the whole picture in one spoken sentence) and a `.note` (the one
  // takeaway line drawn under it). Copy rule: describe what the drawing shows
  // inside the system — never what it predicts.
  'learn.fig.lagna': 'Lagna',
  'learn.fig.rashiWheel.alt':
    'The twelve rashis as a ring, numbered 1 to 12, with Mesha shaded as the start of the count.',
  'learn.fig.rashiWheel.center1': 'The 12 rashis',
  'learn.fig.rashiWheel.center2': '30° each',
  'learn.fig.rashiWheel.note': 'The count always begins at Mesha — rashi 1, shaded.',
  'learn.fig.northLayout.alt':
    'North Indian kundli: a diamond in a square. Bhava 1 sits fixed at the top centre and the count runs anticlockwise.',
  'learn.fig.northLayout.note':
    'Bhavas hold still — bhava 1 is always top centre. The rashis are what move from kundli to kundli.',
  'learn.fig.southLayout.alt':
    'South Indian kundli: a 4-by-4 grid where every cell is permanently one rashi. Bhavas are counted clockwise from the marked lagna.',
  'learn.fig.southLayout.note':
    'Rashis hold still — every cell is always the same rashi. The diagonal stroke marks the lagna; counting runs clockwise from it.',
  'learn.fig.eastLayout.alt':
    'East Indian kundli: a square with split corner cells, every cell permanently one rashi, counted on from the marked lagna.',
  'learn.fig.eastLayout.note':
    'Rashis hold still here too — the split corner cells are what mark this style apart.',
  'learn.fig.grahaParivar.alt':
    'The nine grahas as a grid, each with the one word the tradition most associates with it.',
  'learn.fig.grahaParivar.note':
    'Rahu and Ketu are drawn dashed: crossing points in the sky, not bodies.',
  'learn.fig.nature.sun': 'soul',
  'learn.fig.nature.moon': 'mind',
  'learn.fig.nature.mars': 'drive',
  'learn.fig.nature.mercury': 'speech',
  'learn.fig.nature.jupiter': 'wisdom',
  'learn.fig.nature.venus': 'love',
  'learn.fig.nature.saturn': 'discipline',
  'learn.fig.nature.rahu': 'hunger',
  'learn.fig.nature.ketu': 'release',
  'learn.fig.nakshatraBand.alt':
    'One rashi of 30 degrees spanning two and a quarter nakshatras of 13°20′ each, starting at Ashwini.',
  'learn.fig.nakshatraBand.start': 'The 27-count starts here',
  'learn.fig.nakshatraBand.note':
    'Every rashi holds exactly 2¼ nakshatras — the two grids lock together.',
  'learn.fig.bhavaMap.alt':
    'The twelve bhavas as a map: number, traditional name, and the areas of life each one covers.',
  'learn.fig.bhavaMap.note':
    'Counted from your lagna — bhava 1, shaded, is wherever your lagna is.',
  'learn.fig.dashaBar.alt':
    'The 120-year Vimshottari cycle as one bar, each graha’s period drawn to scale, from Ketu’s 7 years to Shukra’s 20.',
  'learn.fig.dashaBar.total': '120 years in all',
  'learn.fig.dashaBar.years': '{n}y',
  'learn.fig.dashaBar.note':
    'The order never changes — your birth nakshatra only decides where you enter it.',
  'learn.fig.ayanamsa.alt':
    'Two zodiac rings over the same sky, their starting lines offset by about 24 degrees — the ayanamsa.',
  'learn.fig.ayanamsa.sidereal': 'Sidereal — Mesha starts here',
  'learn.fig.ayanamsa.tropical': 'Tropical — Aries starts here',
  'learn.fig.ayanamsa.gap1': 'ayanamsa',
  'learn.fig.ayanamsa.gap2': '≈ 24°',
  'learn.fig.ayanamsa.note':
    'Same sky, two starting lines. The gap grows about one degree every 72 years.',
  'learn.fig.aspectLines.alt':
    'A Western chart wheel with lines showing a conjunction, an opposition, a trine and a square between planets.',
  'learn.fig.aspectLines.conjunction': 'Conjunction · 0°',
  'learn.fig.aspectLines.opposition': 'Opposition · 180°',
  'learn.fig.aspectLines.trine': 'Trine · 120°',
  'learn.fig.aspectLines.square': 'Square · 90°',
  'learn.fig.aspectLines.note':
    'An aspect is an angle between two planets — each angle carries its own traditional reading.',
  'learn.seeOnKundli': 'See this on your kundli',
  // === pro-clarity keys (owner: pro clarity) ===
  // Every number in these strings is interpolated from the entitlement
  // snapshot's config — the strings themselves never carry a quota or price.
  'pro.compare.title': 'Base vs Pro — exactly what changes',
  'pro.compare.intro':
    'Base is the permanent core of Astrally — your kundli, Rashifal, panchanga, Learn, journal, muhurta and an Astrally AI trial. It never expires. Pro adds volume, media and full discovery on top. Credits are the pay-per-reading wallet, and either tier can top them up.',
  'pro.compare.baseCol': 'Base',
  'pro.compare.proCol': 'Pro',
  'pro.compare.price': 'Price',
  'pro.compare.priceBase': '{price} once — yours for good',
  'pro.compare.pricePro': '{monthly}/month or {annual}/year',
  'pro.compare.ally': 'Ally chat',
  'pro.compare.allyBase': '{n} messages a day',
  'pro.compare.allyPro': 'Unlimited within fair use',
  'pro.compare.discovery': 'Discovery',
  'pro.compare.discoveryBase': '{n} profiles a day, previews',
  'pro.compare.discoveryPro': '{n} a day, fully ranked by compatibility',
  'pro.compare.requests': 'Connection requests',
  'pro.compare.requestsBase': '{n} — lifetime, not per month',
  'pro.compare.requestsPro': '{n} every day',
  'pro.compare.chatMedia': 'Photos & videos in chat',
  'pro.compare.chatMediaBase': 'Locked — arrive blurred',
  'pro.compare.chatMediaPro': 'Send and open',
  'pro.compare.feedPhotos': 'Feed photos',
  'pro.compare.feedPhotosBase': 'Blurred previews',
  'pro.compare.feedPhotosPro': 'Post and view',
  'pro.compare.readings': 'Go Deeper / Master readings',
  'pro.compare.readingsBoth': 'Credits — same on both',
  'pro.compare.introCredits': 'Intro credits',
  'pro.compare.introCreditsBase': '{n} included with Base',
  'pro.compare.introCreditsPro': '—',
  'pro.compare.fairUseNote':
    'Fair use means up to {perTenMin} messages in 10 minutes and {perHour} in an hour — normal conversation never touches it.',
  'credits.bothTiers': 'Credits work on Base and Pro — Pro is not required to buy or spend them.',
  'unlock.limit.requests':
    'Base includes {n} lifetime outgoing connection requests. Incoming requests, accepting and existing chats are always unlimited.',
  'unlock.limit.requestsPending':
    'Base includes a lifetime allowance of outgoing connection requests. Incoming requests, accepting and existing chats are always unlimited.',
  'unlock.limit.ally':
    'Astrally AI — {n} free messages a day (they reset at midnight), plus {credits} intro credits',
  'unlock.limit.allyPending': 'Astrally AI with daily free messages + intro credits',

  /* ANCHOR BLOCKS, ROUND 2 — several agents add keys here concurrently and a
     shared "append at the end" point is a write race. Keys go INSIDE the
     owning block only; never append below the last one. */

  // === calendar keys (owner: astro calendar) ===
  // === places keys (owner: places map) ===
  // === deck keys (owner: swipe deck) ===
  // === interests keys (owner: interests picker) ===
  // === partners keys (owner: two AI partners) ===
  // === tutorial keys (owner: guided tutorials) ===
  // === landing keys (owner: marketing landing) ===
  // The public page at astrally.in. Copy rule, harder here than anywhere else:
  // a landing page is where products start promising outcomes. Nothing below
  // says what will HAPPEN to anybody — every line describes what Astrally
  // calculates, what it shows, or what a placement MEANS inside the system.
  'landing.hero.eyebrow': 'Jyotish first · Western when you want it',
  'landing.hero.title': 'Your kundli, and an AI that reads it with you',
  'landing.hero.body':
    'Astrally calculates your birth chart properly — every graha, every bhava, the vargas, the dashas — and then explains it from zero, in plain language, for as long as you want to keep asking.',
  'landing.hero.cta': 'Build my kundli',
  'landing.hero.secondary': 'Look up a word first',
  // TRUE as written, and checked against the gate in App.tsx before it was:
  // /glossary and /learn are exempt from BOTH the onboarding gate and the
  // purchase gate, so a visitor really can read all of both without an
  // account. The kundli itself is not free, so this line does not claim it is.
  // No price is quoted anywhere on this page: the real price lives in remote
  // config and a hardcoded number here could go stale into a lie.
  'landing.trust':
    'The full glossary and the Learn course are free to read — no account, no card. The app itself opens with a single one-time unlock.',
  'landing.vocab.title': 'Words this page uses, each one tappable',

  'landing.s1.eyebrow': 'The chart',
  'landing.s1.title': 'Your kundli — every graha, bhava and varga',
  'landing.s1.body':
    'The real calculation, not a lookup table: sidereal positions on the ayanamsa you choose, the twelve bhavas counted from your Lagna, the divisional charts, and the yogas that genuinely form in your chart. Every term on the screen explains itself when you tap it.',
  'landing.s1.b1': 'North, South and East Indian styles — the one you grew up with',
  'landing.s1.b2': 'D1 to D60 vargas, with the Western wheel alongside if you want it',
  'landing.s1.b3': 'No birth time? It shows what still holds and what honestly does not',
  'landing.s1.cta': 'Open a kundli',

  'landing.s2.eyebrow': 'The astrologer',
  'landing.s2.title': 'Ally, your AI astrologer',
  'landing.s2.body':
    'Ally reads from your calculated chart, not from a column written for a twelfth of the planet. Ask in your own words; every answer can show you the exact chart values it was built on.',
  'landing.s2.b1': 'Grounded in your own numbers, with the sources one tap away',
  'landing.s2.b2': 'Remembers your conversations, so you never explain yourself twice',
  'landing.s2.b3': 'Guided paths for the days you have no idea what to ask',
  'landing.s2.cta': 'Meet Ally',

  'landing.s3.eyebrow': 'Every morning',
  'landing.s3.title': 'Aaj ka rashifal, from your Chandra rashi',
  'landing.s3.body':
    'Your daily reading is cast from your Moon rashi and the gochara actually running today — not from your sun sign. The panchanga sits beside it: tithi, vaara, nakshatra and the day’s Rahu kaal for your own city.',
  'landing.s3.b1': 'Daily, weekly, monthly and yearly, in both systems',
  'landing.s3.b2': 'Panchanga and Rahu kaal computed for where you are',
  'landing.s3.b3': 'Rate what landed — the app keeps score of its own accuracy',
  'landing.s3.cta': 'See today’s rashifal',

  'landing.s4.eyebrow': 'Two charts',
  'landing.s4.title': 'Kundali Milan — Ashtakoota out of 36',
  'landing.s4.body':
    'All eight kootas scored the traditional way, and every one of them shown with its reasoning: what it measures, what the two charts scored, and why. Doshas are explained as what they mean inside the system — never handed down as a verdict.',
  'landing.s4.b1': 'Eight kootas, itemised, out of 36 — with the working shown',
  'landing.s4.b2': 'Manglik and the doshas explained, not weaponised',
  'landing.s4.b3': 'Western synastry side by side, for a second reading',
  'landing.s4.cta': 'Try Kundali Milan',

  'landing.s5.eyebrow': 'Your year',
  'landing.s5.title': 'An astro calendar cast on your own chart',
  'landing.s5.body':
    'Gochara touching your placements, the day a dasha turns, and the windows that are genuinely clear for something you are planning. Dates read off your kundli, not off a generic almanac.',
  'landing.s5.b1': 'Gochara and dasha turns marked against your own placements',
  'landing.s5.b2': 'Muhurta windows for a specific thing, with the reasoning',
  'landing.s5.b3': 'Switch the same grid to Western transits whenever you like',
  'landing.s5.cta': 'Start your kundli',

  'landing.s6.eyebrow': 'Pro',
  'landing.s6.title': 'Places — where your grahas run angular',
  'landing.s6.body':
    'Every graha traces lines across the world: the meridians where it sat on an angle at the moment you were born. It is a way of asking what a place brings out in your kundli — never an instruction to move.',
  'landing.s6.b1': 'Your graha lines drawn on a map that ships inside the app',
  'landing.s6.b2': 'Works with no signal; nothing about you leaves the device to draw it',
  'landing.s6.b3': 'A plain-language reading for any city you are curious about',
  'landing.s6.cta': 'See what Pro adds',

  // THE DEFINITION BLOCK. These four strings are typeset as a dictionary entry
  // and are asserted verbatim by landing.test.tsx — the wording is the brand
  // moment, so it does not drift by accident.
  'landing.def.word': 'astrally',
  'landing.def.ipa': '/ˈas.trə.li/',
  'landing.def.pos': 'adverb',
  'landing.def.sense': 'in a manner relating to the stars',
  'landing.def.note':
    'We picked an adverb on purpose. A noun would have been a thing handed to you — a fate, a verdict, a score. An adverb is a way of doing something. You read a chart astrally the way you read a map: it tells you what the ground is like, and where you walk is still yours.',

  'landing.mock.chatQ': 'Shani is in my 7th bhava — what is it asking of me?',
  'landing.mock.chatA':
    'Shani in the 7th is the bhava of partnership under the graha of slow, exacting work. In this system it reads as commitment tested by time rather than by drama — and it is one placement among many, so let us look at what the rest of your kundli says about it.',
  'landing.mock.sources': '3 chart sources',
  'landing.mock.rashifalTitle': 'Aaj ka rashifal',
  'landing.mock.rashifalRashi': 'Chandra rashi · Vrishabha',
  'landing.mock.rashifalBody':
    'Guru moves through your 11th bhava today. In this system that is the house of gain and of the people around you — a day to notice what your circle is asking of you.',
  'landing.mock.kundliCaption': 'North Indian style · bhavas fixed, rashis rotating',
  'landing.mock.milanScore': '28 / 36',
  'landing.mock.calendarCaption': 'Your next 30 days, read off your own chart',
  'landing.mock.placesCaption': 'Graha lines, drawn on-device',

  'landing.foot.tagline': 'Made in India, for anyone who wants the real thing explained properly.',
  'landing.foot.glossary': 'Glossary',
  'landing.foot.learn': 'Learn astrology from zero',
  'landing.foot.privacy': 'Your data, your rules',

  // ---------------------------------------------------------------------------
  // feed-engagement keys
  // ---------------------------------------------------------------------------
  // Likes and comments on the members-only feed. Kept as one block at the end
  // of the object on purpose: this file is edited by several changes at once,
  // and a uniquely-named block appended here merges cleanly where an insertion
  // into the middle would conflict.
  //
  // The moderation strings are NOT redefined here — a blocked comment reuses
  // `msg.moderation.blocked` / `.unavailable`, the same copy a blocked message
  // and a blocked post already show. One refusal, worded once.
  'feed.like': 'Like',
  'feed.unlike': 'Remove like',
  // Read by a screen reader on the heart button, so it has to say the count
  // AND the state; the visible label is just the number next to the icon.
  'feed.likeCount': '{n} likes',
  'feed.likeFailed': 'That didn’t register. Try again.',
  'feed.comment': 'Comment',
  'feed.commentCount': '{n} comments',
  'feed.comments.title': 'Comments',
  'feed.comments.empty': 'No comments yet. Say the first thing.',
  'feed.comments.placeholder': 'Write a comment…',
  'feed.comments.post': 'Post',
  'feed.comments.close': 'Close',
  'feed.comments.loadMore': 'Show older comments',
  'feed.comments.loadFailed': 'Those comments didn’t load.',
  'feed.comments.delete': 'Delete',
  'feed.comments.deleteFailed': 'That didn’t delete. Try again.',
  'feed.comments.failed': 'That comment didn’t post. Try again.',
  'feed.comments.tooLong': 'A comment can be at most 500 characters.',
  // Shown under a comment nothing inspected — the same honesty the composer
  // applies to photos. Never phrased as a warning about the author.
  'feed.comments.unchecked': 'Not automatically checked',
} as const;

export type I18nKey = keyof typeof en;
